import Clarity from '@microsoft/clarity';
import { hasAnalyticsConsent } from '@/lib/analyticsConsent';

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
  }
}

// Allow-listed product events only. Event names must never embed an identifier
// (id, email, title, ref). Add new events here deliberately -- this list is the
// enforcement point that keeps direct identifiers out of Clarity.
export type AnalyticsEventName =
  | 'signup_started'
  | 'signup_completed'
  | 'login_completed'
  | 'story_created'
  | 'journal_entry_created'
  | 'life_card_created'
  | 'journey_marker_created'
  | 'family_member_added'
  | 'circle_created'
  | 'credential_verification_started'
  | 'credential_verification_completed'
  | 'subscription_checkout_started'
  | 'people_search_opened'
  | 'people_search_result_opened'
  | 'profile_reach_out_started'
  | 'message_request_sent'
  | 'message_request_accepted'
  | 'conversation_opened'
  | 'messages_load_failed'
  | 'profile_link_copied'
  | 'living_record_recommendation_viewed'
  | 'living_record_recommendation_opened'
  | 'living_record_recommendation_completed'
  | 'living_record_recommendation_dismissed'
  | 'record_connection_created'
  | 'institution_action_opened'
  | 'institution_action_completed'
  | 'landing_living_record_demo_started'
  | 'landing_living_record_demo_step'
  | 'landing_living_record_demo_completed'
  | 'ai_milestone_opened'
  | 'ai_milestone_generation_requested'
  | 'ai_milestone_generation_succeeded'
  | 'ai_milestone_generation_failed'
  | 'ai_milestone_saved_as_draft'
  | 'ai_milestone_created'
  | 'ai_milestone_cancelled';

// Allow-listed, low-cardinality categorical tags only. Values must be a fixed
// category (e.g. a role or plan name), never a name, email, or any per-user ID.
export type AnalyticsTagKey = 'account_type' | 'plan_tier' | 'locale' | 'route_group' | 'theme';

function canSendToClarity(): boolean {
  return (
    typeof window !== 'undefined' && typeof window.clarity === 'function' && hasAnalyticsConsent()
  );
}

export function trackEvent(name: AnalyticsEventName): void {
  try {
    if (!canSendToClarity()) return;
    Clarity.event(name);
  } catch {
    // Analytics must never break the product.
  }
}

export function setAnalyticsTag(key: AnalyticsTagKey, value: string | string[]): void {
  try {
    if (!canSendToClarity()) return;
    Clarity.setTag(key, value);
  } catch {
    // Analytics must never break the product.
  }
}
